<script setup lang="ts">
import { X } from '@lucide/vue'
import {
    Button,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ZhPanel,
    ZhStatusDot,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import type { Member, MemberTeam, Role } from '@/api'
import MemberAvatar from '@/components/MemberAvatar.vue'
import { useUiMode } from '@/composables/useUiMode'
import { memberLabel } from '@/lib/memberLabel'
import { pickedId } from '@/lib/memberPickers'
import { type RunStatusTone, statusLabel, statusTone } from '@/lib/statusTone'
import type { TeammateTools } from '@/lib/teammateTools'

/**
 * One teammate inside Team (#1746): their name, their job (the roles they hold), the teams they are on, what they are
 * working on and the tools that serve them, on a kit surface of its own. The job is the one thing edited here: a role
 * is picked from the kit Select and a chip's delete takes one away, both through the calls the member page makes,
 * which the screen holding the card performs. Everything else is edited on the member page the name links to.
 *
 * Made only of kit pieces: the chips are kit system labels, the dot the kit's, the picker the kit Select. The card
 * states layout and nothing else, no colour, radius, border or fill of its own.
 */
const props = defineProps<{
    member: Member
    /** Every role there is to pick from, or null when the list could not be read: the job is then read-only. */
    roles: Role[] | null
    /** The tools that serve this teammate, read once for the whole page. */
    tools: TeammateTools
    /** A role change of this teammate's is under way. */
    busy?: boolean
    /** Why the last role change on this card failed, shown on this card only. */
    error?: string | null
}>()

const emit = defineEmits<{
    assign: [roleId: string]
    remove: [roleId: string]
}>()

const label = computed(() => memberLabel(props.member))
const teams = computed<MemberTeam[]>(() => props.member.teams ?? [])
const editable = computed(() => props.roles !== null)

/** The roles the teammate does not hold yet: the only ones the picker offers. */
const availableRoles = computed(() => {
    const held = new Set(props.member.roles.map(role => role.id))
    return (props.roles ?? []).filter(role => !held.has(role.id))
})

/** A run status's tone, as the state the kit dot takes (BG §18.4: the same five states, by their dot names). */
const DOT_STATE: Record<RunStatusTone, 'active' | 'complete' | 'inactive' | 'failed' | 'action'> = {
    running: 'active',
    completed: 'complete',
    neutral: 'inactive',
    failed: 'failed',
    action: 'action',
}

const dotState = computed(() => DOT_STATE[statusTone(props.member.activeRun?.status)])

const { mode } = useUiMode()

/** A run status as the product says it out loud in Standard, and as stored in Advanced, where machine values show. */
function runStatusText(status: string): string {
    return mode.value === 'advanced' ? status : statusLabel(status)
}

/** A team the teammate leads says so; any other is its name. */
function teamText(team: MemberTeam): string {
    return team.lead ? `Lead · ${team.name}` : team.name
}

const toolsText = computed(() => {
    if (props.tools.status === 'failed') return 'Could not load tools'
    if (props.tools.status === 'loading') return 'Loading tools…'
    return 'No tools connected'
})

/** A role picked is assigned at once; the picker itself never holds the choice. */
function pick(choice: unknown): void {
    const roleId = pickedId(choice)
    if (roleId) emit('assign', roleId)
}
</script>

<template>
	<ZhPanel as="article" :data-testid="`teammate-card-${member.id}`" class="flex flex-col gap-4">
		<h2 class="text-base font-medium">
			<RouterLink :to="`/members/${member.id}`" class="flex items-center gap-3 underline-offset-2 hover:underline">
				<MemberAvatar :member="member" class="size-10" />
				<span>{{ label }}</span>
			</RouterLink>
		</h2>
		<dl class="grid gap-3 text-sm">
			<div class="grid gap-1">
				<dt class="text-muted-foreground text-xs">Job</dt>
				<dd class="flex flex-wrap items-center gap-2">
					<span v-for="role in member.roles" :key="role.id" class="inline-flex items-center gap-1">
						<ZhSystemLabel as="span" data-testid="role-badge">{{ role.name }}</ZhSystemLabel>
						<Button
							v-if="editable"
							type="button"
							variant="ghost"
							size="icon-sm"
							:disabled="busy"
							:aria-label="`Remove ${role.name}`"
							@click="emit('remove', role.id)"
						>
							<X aria-hidden="true" />
						</Button>
					</span>
					<span v-if="!member.roles.length" class="text-muted-foreground">No job yet</span>
					<Select v-if="editable && availableRoles.length" model-value="" :disabled="busy" @update:model-value="pick">
						<SelectTrigger size="sm" :aria-label="`Give ${label} a job`">
							<SelectValue placeholder="Add a job…" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem v-for="role in availableRoles" :key="role.id" :value="role.id">{{ role.name }}</SelectItem>
						</SelectContent>
					</Select>
					<!-- With no picker, say why: a change under way, a role list that could not be read, or nothing left to give. -->
					<span v-if="busy" class="text-muted-foreground">Saving…</span>
					<span v-else-if="!editable" class="text-muted-foreground">Jobs can’t be changed right now</span>
					<span v-else-if="!availableRoles.length" class="text-muted-foreground">
						No other jobs to add. Jobs are made under Roles, in Advanced mode.
					</span>
				</dd>
			</div>
			<div class="grid gap-1">
				<dt class="text-muted-foreground text-xs">Team</dt>
				<dd v-if="teams.length" class="flex flex-wrap items-center gap-2">
					<ZhSystemLabel v-for="team in teams" :key="team.id" as="span">{{ teamText(team) }}</ZhSystemLabel>
				</dd>
				<dd v-else class="text-muted-foreground">Not on a team</dd>
			</div>
			<div class="grid gap-1">
				<dt class="text-muted-foreground text-xs">Working on</dt>
				<dd v-if="member.activeRun" class="flex flex-wrap items-center gap-2">
					<RouterLink :to="`/runs/${member.activeRun.id}`" class="underline-offset-2 hover:underline">{{ member.activeRun.taskName }}</RouterLink>
					<ZhSystemLabel as="span" tone="system" data-testid="active-indicator" class="inline-flex items-center gap-1">
						<ZhStatusDot :state="dotState" />{{ runStatusText(member.activeRun.status) }}
					</ZhSystemLabel>
				</dd>
				<dd v-else class="text-muted-foreground">Not working on anything right now</dd>
			</div>
			<div class="grid gap-1">
				<dt class="text-muted-foreground text-xs">Tools</dt>
				<dd v-if="tools.status === 'ready' && tools.labels.length" class="flex flex-wrap items-center gap-2">
					<ZhSystemLabel v-for="tool in tools.labels" :key="tool" as="span">{{ tool }}</ZhSystemLabel>
				</dd>
				<dd v-else class="text-muted-foreground">{{ toolsText }}</dd>
			</div>
		</dl>
		<p v-if="error" role="alert" class="text-destructive text-sm">{{ error }}</p>
	</ZhPanel>
</template>
